import * as amplitude from "@amplitude/analytics-browser";
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Flex,
  Skeleton,
  Text,
} from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import React, { useMemo } from "react";
import { GenericError } from "src/components/Feedback/GenericError";
import { RemoteDataView } from "src/components/Layout/RemoteDataView";
import { rowExpanderColumnDef } from "src/components/Table/rowExpanderColumnDef";
import {
  ColumnDefWithTooltip,
  Table,
  TableHeaderProps,
} from "src/components/Table/Table";
import { useGetCapacitiesHistoryDetailKey } from "src/generated/hooks/form-service/queries";
import {
  getCapacitiesHistoryDetail,
  GetCapacitiesHistoryDetailResponse,
} from "src/generated/hooks/form-service/requests";
import * as RD from "src/types/remoteData";

export type Props = {
  enrollmentPeriodId: string;
  transactionId: number;
};

// Grades are top-level rows; programs are sub-rows.  Both share this shape
// so that getSubRows and rowExpanderColumnDef work with a single type.
type CapacityRow = {
  id: string;
  name: string;
  capacity: { newValue: number; delta: number } | null;
  additionalSeatsTaken1?: { newValue: number; delta: number } | null;
  additionalSeatsTaken2?: { newValue: number; delta: number } | null;
  programs?: CapacityRow[];
};

type Config = {
  additionalSeatsTaken1: { enabled: boolean; label: string };
  additionalSeatsTaken2: { enabled: boolean; label: string };
};

type SchoolData = {
  schoolId: string;
  schoolLabel: string;
  rows: CapacityRow[];
};

export const HistoryDetail: React.FC<Props> = ({
  enrollmentPeriodId,
  transactionId,
}) => {
  const query = useQuery({
    queryKey: [
      useGetCapacitiesHistoryDetailKey,
      { path: { enrollmentPeriodId, transactionId: String(transactionId) } },
    ],
    queryFn: () =>
      getCapacitiesHistoryDetail({
        path: {
          enrollmentPeriodId,
          transactionId: String(transactionId),
        },
      }).then((response) => response.data),
  });

  const detailRD: RD.RemoteData<
    Error | null,
    GetCapacitiesHistoryDetailResponse
  > = useMemo(() => RD.fromRestResult(query), [query]);

  return (
    <RemoteDataView
      remoteData={detailRD}
      error={(error) => <GenericError error={error as Error} />}
      loading={<DetailLoading />}
    >
      {(data) => {
        const config: Config = data.data.config;

        const schools: SchoolData[] = data.data.capacities.map((entry) => ({
          schoolId: entry.schools.id,
          schoolLabel: entry.schools.label,
          rows: entry.schools.grades
            .sort((a, b) => a.order - b.order)
            .map((grade) => {
              const hasPrograms = grade.programs.length > 0;
              return {
                id: grade.id,
                name: grade.gradeConfig.label,
                capacity: hasPrograms
                  ? null
                  : {
                      newValue: grade.capacity.newValue,
                      delta: grade.capacity.delta,
                    },
                additionalSeatsTaken1: hasPrograms
                  ? null
                  : grade.additionalSeatsTaken1
                  ? {
                      newValue: grade.additionalSeatsTaken1.newValue,
                      delta: grade.additionalSeatsTaken1.delta,
                    }
                  : null,
                additionalSeatsTaken2: hasPrograms
                  ? null
                  : grade.additionalSeatsTaken2
                  ? {
                      newValue: grade.additionalSeatsTaken2.newValue,
                      delta: grade.additionalSeatsTaken2.delta,
                    }
                  : null,
                programs: grade.programs
                  .sort((a, b) =>
                    a.program.label.localeCompare(b.program.label)
                  )
                  .map((prog) => ({
                    id: prog.program.id,
                    name: prog.program.label,
                    capacity: {
                      newValue: prog.capacity.newValue,
                      delta: prog.capacity.delta,
                    },
                    additionalSeatsTaken1: prog.additionalSeatsTaken1
                      ? {
                          newValue: prog.additionalSeatsTaken1.newValue,
                          delta: prog.additionalSeatsTaken1.delta,
                        }
                      : null,
                    additionalSeatsTaken2: prog.additionalSeatsTaken2
                      ? {
                          newValue: prog.additionalSeatsTaken2.newValue,
                          delta: prog.additionalSeatsTaken2.delta,
                        }
                      : null,
                  })),
              };
            }),
        }));

        return (
          <Flex direction="column" gap="12" pt="6">
            {schools.map((school) => (
              <SchoolTable
                key={school.schoolId}
                school={school}
                config={config}
              />
            ))}
          </Flex>
        );
      }}
    </RemoteDataView>
  );
};

// Replaces the default Table header (which renders an Expand/Collapse all
// button whenever getSubRows is provided) with nothing.
const EmptyHeader: React.FC<TableHeaderProps> = () => null;

// Each school is its own component so that sort state is isolated per table
// and sorting one school does not re-render the others.
const SchoolTable: React.FC<{ school: SchoolData; config: Config }> = ({
  school,
  config,
}) => {
  const columns: ColumnDefWithTooltip<CapacityRow>[] = useMemo(() => {
    const cols: ColumnDefWithTooltip<CapacityRow>[] = [
      rowExpanderColumnDef({
        id: "name",
        header: "Grade",
        cell: ({ row }) => {
          if (row.depth > 0) {
            return <Text fontStyle="italic">{row.original.name}</Text>;
          }
          return row.original.name;
        },
      }),
      {
        id: "capacity",
        header: "Total capacity",
        cell: ({ row }) => {
          const cap = row.original.capacity;
          if (!cap) return null;
          const delta = cap.delta > 0 ? `+${cap.delta}` : `${cap.delta}`;
          return (
            cap.delta !== 0 && (
              <Flex justifyContent="right">
                {cap.newValue} ({delta})
              </Flex>
            )
          );
        },
      },
    ];

    if (config.additionalSeatsTaken1.enabled) {
      cols.push({
        id: "additionalSeatsTaken1",
        header: config.additionalSeatsTaken1.label,
        cell: ({ row }) => {
          const ast1 = row.original.additionalSeatsTaken1;
          if (!ast1) return null;
          const delta = ast1.delta > 0 ? `+${ast1.delta}` : `${ast1.delta}`;
          return (
            ast1.delta !== 0 && (
              <Flex justifyContent="right">
                {ast1.newValue} ({delta})
              </Flex>
            )
          );
        },
      });
    }

    if (config.additionalSeatsTaken2.enabled) {
      cols.push({
        id: "additionalSeatsTaken2",
        header: config.additionalSeatsTaken2.label,
        cell: ({ row }) => {
          const ast2 = row.original.additionalSeatsTaken2;
          if (!ast2) return null;
          const delta = ast2.delta > 0 ? `+${ast2.delta}` : `${ast2.delta}`;
          return (
            ast2.delta !== 0 && (
              <Flex justifyContent="right">
                {ast2.newValue} ({delta})
              </Flex>
            )
          );
        },
      });
    }

    return cols;
  }, [config]);

  return (
    <Accordion allowMultiple defaultIndex={[0]}>
      <AccordionItem
        border="1px solid"
        borderColor="gray.200"
        borderRadius="md"
      >
        <AccordionButton
          bg="white"
          _hover={{ bg: "gray.50" }}
          _expanded={{ borderBottomRadius: 0 }}
          borderRadius="md"
          px={4}
          py={6}
          alignItems="center"
        >
          <Text flex="1" textAlign="left" fontWeight="bold" color="primary.500">
            {school.schoolLabel}
          </Text>
          <AccordionIcon />
        </AccordionButton>
        <AccordionPanel p={0} borderTop="1px solid" borderColor="gray.200">
          <Table<CapacityRow>
            data={school.rows}
            columns={columns}
            tableHeader={EmptyHeader}
            expandAllProps={{
              expandAllSubRows: true,
              onToggle: (toggle) => {
                if (toggle === "expand") {
                  amplitude.track("Clicked expand all");
                } else {
                  amplitude.track("Clicked collapse all");
                }
              },
            }}
            getSubRows={(row) => row.programs}
            tablePadding="0"
            tableContainerProps={{ rounded: "none", padding: 0 }}
            getHeaderStyleProps={(header) => ({
              textAlign: "center",
              ...(header.id !== "name"
                ? { borderLeftWidth: "1px", borderLeftColor: "gray.200" }
                : {}),
            })}
            getCellStyleProps={(cell) => {
              return cell.column.id !== "name"
                ? { borderLeftWidth: "1px", borderLeftColor: "gray.200" }
                : {};
            }}
          />
        </AccordionPanel>
      </AccordionItem>
    </Accordion>
  );
};

const DetailLoading: React.FC = () => (
  <Flex direction="column" gap={4} pt={6}>
    <Skeleton height="400px" />
  </Flex>
);
